import { user } from 'utils/auth/user';
import type { TPrString } from 'data/schema';
import { runApplyingRemote } from './remoteApplyGuard';
import { announceDeckPrString } from './deckPrStringHold';
import type { DeckAnnouncement } from './deckSelection';
import { applyAnnouncedPrString, applyStandaloneAnnouncedPrString } from './vehicleRegistry';

/**
 * Apply a 3D Showroom announcement (`update:pr-string`, `update:consumption-data`).
 *
 * A customer's Showroom mirrors the dealer's car, so a customer applies it locally and syncs
 * nothing, emit-only included (AVS-4412). It runs as a remote apply, the switch `syncToServer`
 * already honours; a write already pending from a real customer edit still goes out.
 * A dealer keeps saving: the dealer's write is how an announced car reaches the record.
 */
export function runShowroomAnnouncement(apply: () => void): void {
  if (user.isDealer) {
    apply();
    return;
  }
  runApplyingRemote(apply);
}

/**
 * Receive a 3D Showroom `update:pr-string` (AVS-4422): the one entry point for both hosts.
 *
 * A late announcement of a car a broadcast already moved past changes nothing (AVS-4462). Otherwise
 * it applies as a Showroom announcement: DCC and web components hand it to the registry, which keeps
 * the `vinOrMarketingCode` DCC supplies itself (AVS-4399); standalone resets the car it replaces.
 */
export function receiveShowroomPrString(
  prString: TPrString | undefined,
  announcement: DeckAnnouncement | undefined,
  { webComponent }: { webComponent: boolean }
): void {
  if (announceDeckPrString(prString)) return;
  runShowroomAnnouncement(() =>
    webComponent
      ? applyAnnouncedPrString(prString, announcement)
      : applyStandaloneAnnouncedPrString(prString, announcement)
  );
}
